import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  Snackbar,
  Alert,
  Autocomplete,
  Dialog,
  InputAdornment,
  IconButton,
  Slider,
  useTheme,
  useMediaQuery,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import theme from "../theme";
import axios from "axios";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import { isServiceHead } from "../utils/roles";
import { useNavigate } from "react-router-dom";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

// Icon
import success from "../assets/icons/success.svg";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EmojiFlagsRoundedIcon from "@mui/icons-material/EmojiFlagsRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import LoadingScreen from "./LoadingScreen";
import { whatsAppFailureText } from "../services/wpService";
import TicketDetails from "./TicketDetails";
import { useTimer } from "../Context/TimerContext";

const TicketManagementForm = ({ ticketData }) => {
  const resTheme = useTheme();
  const isMobile = useMediaQuery(resTheme.breakpoints.down("md"));
  const navigate = useNavigate();
  const ticket = ticketData.ticket;

  const ticketInfo =
    ticketData.ticket_info.length > 0 ? ticketData.ticket_info[0] : null;
  const ticketID = ticket?.id;
  const pm_id = ticket.pm_id || null;

  const editTimeBudget = ticketInfo?.time_budget || 0;

  const contextData = useContext(AutoTaskAuthContext);
  const timer = useTimer();
  const { allKumuduDepartments, allInternalDepartments, allUsers } =
    contextData;
  const userInfo = contextData?.userInfo;
  const user_id = userInfo?.id;
  const allStaff = allUsers;

  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [overlayFile, setOverlayFile] = useState(null);

  const [serviceTicketDetails, setServiceTicketDetails] = useState({
    teamMember: "",
    servicePriorityLevel: ticket?.priority,
    status: "in_progress",
    dueDate: ticket.deadline,
    timeBudget: ticketInfo?.time_budget || "",
    serviceDescription: "",
    serviceAudioDescription: "",
    serviceImages: [],
    timeTaken: "",
    startDate: formatDate(ticket.start_date),
  });

  // Snackbar config
  const [snackBarConfig, setSnackBarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });

  // Close Snackbar
  const handleClose = (e, reason) => {
    if (reason === "clickaway") return;
    setSnackBarConfig((prevState) => ({ ...prevState, open: false }));
  };

  // Lock assignee to the logged-in user when status is follow_up
  useEffect(() => {
    if (serviceTicketDetails.status === "follow_up") {
      setServiceTicketDetails((prev) => ({
        ...prev,
        teamMember: user_id,
      }));
    }
  }, [serviceTicketDetails.status, user_id]);

  // Get the initial status and info of the ticket
  useEffect(() => {
    if (ticket) {
      const currentStatus =
        ticket.status === "open" ? "in_progress" : ticket.status;

      setServiceTicketDetails({
        teamMember: ticket.assigned_to || user_id || "",
        servicePriorityLevel: ticket.priority || "",
        status: currentStatus,
        dueDate: formatDate(ticket.deadline) || "",
        timeBudget: ticketInfo?.time_budget || 0,
        serviceDescription: "",
        serviceAudioDescription: ticket.serviceAudioDescription || "",
        serviceImages: [],
        startDate: formatDate(ticket.start_date) || "",
        timeTaken: "",
      });
    }
  }, [ticket, ticketInfo]);

  // Generic handler for all text/select fields
  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setServiceTicketDetails((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };

      // ✅ Conditional logic outside the return object
      if (field === "timeTaken" && editTimeBudget === 0) {
        updated.timeBudget = value;
      }

      return updated;
    });
  };

  // ------------------ FOR AUDIO RECORD FEATURE ------------------
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordTimerRef = useRef(null);
  const [audioURL, setAudioURL] = useState("");

  const [isRecording, setIsRecording] = useState(false);
  const [recordTime, setRecordTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);

  const audioRef = useRef(null);

  const handleMicClick = async () => {
    if (!isRecording) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        let mimeType = "";
        if (MediaRecorder.isTypeSupported("audio/mp4")) {
          mimeType = "audio/mp4";
        } else if (MediaRecorder.isTypeSupported("audio/mpeg")) {
          mimeType = "audio/mpeg";
        } else if (MediaRecorder.isTypeSupported("audio/ogg; codecs=opus")) {
          mimeType = "audio/ogg; codecs=opus";
        } else if (MediaRecorder.isTypeSupported("audio/webm; codecs=opus")) {
          mimeType = "audio/webm; codecs=opus";
        }

        mediaRecorderRef.current = new MediaRecorder(
          stream,
          mimeType ? { mimeType } : {},
        );
        audioChunksRef.current = [];

        mediaRecorderRef.current.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mediaRecorderRef.current.onstop = () => {
          const audioBlob = new Blob(audioChunksRef.current, {
            type: mimeType || "audio/webm",
          });
          setServiceTicketDetails((prev) => ({
            ...prev,
            serviceAudioDescription: audioBlob,
          }));

          clearInterval(recordTimerRef.current);
          setRecordTime(0);
        };

        mediaRecorderRef.current.start();
        setIsRecording(true);

        recordTimerRef.current = setInterval(() => {
          setRecordTime((prev) => prev + 1);
        }, 1000);
      } catch (err) {
        console.error("Microphone error:", err);
      }
    } else {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordTimerRef.current);
    }
  };

  useEffect(() => {
    if (serviceTicketDetails?.serviceAudioDescription) {
      const url = URL.createObjectURL(
        serviceTicketDetails.serviceAudioDescription,
      );
      setAudioURL(url);

      return () => {
        URL.revokeObjectURL(url);
        setAudioURL("");
      };
    }
  }, [serviceTicketDetails.serviceAudioDescription]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioURL) return;

    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleTimeUpdate = () => setProgress(audio.currentTime);

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [audioURL]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => console.error("Audio playback failed:", err));
    }
  };

  const handleSliderChange = (_, value) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = value;
    setProgress(value);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const formatTime = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(Math.floor(seconds % 60)).padStart(2, "0");
    return `${m}:${s}`;
  };
  // --------------------------------------------------------------

  // ------------------ FOR IMAGE UPLOAD FEATURE ------------------
  const [isZoomed, setIsZoomed] = useState(false);

  // For pasting an image
  useEffect(() => {
    const handlePaste = (event) => {
      const items = event.clipboardData.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith("image/")) {
          const blob = item.getAsFile();

          const fileWithPreview = {
            file: blob,
            preview: URL.createObjectURL(blob),
            name: `pasted-image-${Date.now()}.png`,
            type: blob.type,
          };

          setServiceTicketDetails((prev) => ({
            ...prev,
            serviceImages: [...prev.serviceImages, fileWithPreview].slice(0, 5),
          }));

          setSnackBarConfig({
            open: true,
            message: "Image pasted successfully",
            severity: "success",
          });

          break;
        }
      }
    };

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, []);

  // Dropzone logic
  const onDrop = (acceptedFiles) => {
    const newFiles = acceptedFiles.map((file) =>
      Object.assign(file, { preview: URL.createObjectURL(file) }),
    );
    setServiceTicketDetails((prev) => ({
      ...prev,
      serviceImages: [...prev.serviceImages, ...newFiles].slice(0, 5),
    }));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "video/mp4": [],
      "video/x-matroska": [],
      "application/pdf": [],
      "application/msword": [],
    },
    maxSize: 10 * 1024 * 1024,
    multiple: true,
    disabled: serviceTicketDetails.serviceImages.length >= 5,
  });

  // Delete an image after uploading
  const handleDeleteImage = (index) => {
    setServiceTicketDetails((prev) => ({
      ...prev,
      serviceImages: prev.serviceImages.filter((_, i) => i !== index),
    }));
  };

  // Handle clicking a file to open dialog
  const handleFileClick = (file) => {
    setOverlayFile(file);
  };

  // Reset zoom when overlayFile changes
  useEffect(() => {
    setIsZoomed(false);
  }, [overlayFile]);
  // --------------------------------------------------------------

  // Date formatter
  function formatDate(dateStr) {
    if (!dateStr) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;

    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";

    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  const priorityOptions = [
    {
      label: "Low",
      value: "low",
      icon: (
        <EmojiFlagsRoundedIcon
          sx={{ fill: theme.lightTheme.prioritieIndicators.low }}
        />
      ),
    },
    {
      label: "Medium",
      value: "medium",
      icon: (
        <EmojiFlagsRoundedIcon
          sx={{ fill: theme.lightTheme.prioritieIndicators.medium }}
        />
      ),
    },
    {
      label: "High",
      value: "high",
      icon: (
        <EmojiFlagsRoundedIcon
          sx={{ fill: theme.lightTheme.prioritieIndicators.high }}
        />
      ),
    },
  ];

  const baseStatusOptions = [
    { label: "In Progress", value: "in_progress" },
    { label: "Resolved", value: "resolved" },
    { label: "Rejected", value: "rejected" },
    { label: "Follow Up", value: "follow_up" },
  ];

  const statusOptions =
    isServiceHead(userInfo?.role)
      ? baseStatusOptions
      : baseStatusOptions.filter((opt) => opt.value !== "follow_up");

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "low":
        return theme.lightTheme.prioritieIndicators.low;
      case "medium":
        return theme.lightTheme.prioritieIndicators.medium;
      case "high":
        return theme.lightTheme.prioritieIndicators.high;
      default:
        return "#000000";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "in_progress":
        return theme.lightTheme.statuses.in_progress;
      case "resolved":
        return theme.lightTheme.statuses.resolved;
      case "follow_up":
        return theme.lightTheme.statuses.follow_up;
      case "recurring":
        return theme.lightTheme.statuses.recurring;
      case "rejected":
        return theme.lightTheme.statuses.rejected;
      default:
        return "#000";
    }
  };

  // Save ticket handler
  const handleSave = async () => {
    const {
      startDate,
      dueDate,
      timeBudget,
      timeTaken,
      servicePriorityLevel,
      status,
    } = serviceTicketDetails;

    if (!startDate) {
      setSnackBarConfig({
        open: true,
        message: "Start date is required",
        severity: "error",
      });
      return;
    }
    if (!dueDate) {
      setSnackBarConfig({
        open: true,
        message: "Due date is required",
        severity: "error",
      });
      return;
    }
    if (!timeBudget) {
      setSnackBarConfig({
        open: true,
        message: "Time budget is required",
        severity: "error",
      });
      return;
    }
    if (!timeTaken) {
      setSnackBarConfig({
        open: true,
        message: "Time taken is required",
        severity: "error",
      });
      return;
    }
    if (!servicePriorityLevel) {
      setSnackBarConfig({
        open: true,
        message: "Priority is required",
        severity: "error",
      });
      return;
    }
    if (!status) {
      setSnackBarConfig({
        open: true,
        message: "Status is required",
        severity: "error",
      });
      return;
    }

    setIsLoading(true);
    const assignFormData = new FormData();
    assignFormData.append("ticket_id", ticketID);
    assignFormData.append("pm_id", pm_id);
    assignFormData.append("assigned_to", serviceTicketDetails.teamMember);
    assignFormData.append(
      "description",
      serviceTicketDetails.serviceDescription,
    );
    // assignFormData.append("status", serviceTicketDetails.status);
    assignFormData.append("time_budget", serviceTicketDetails.timeBudget);
    assignFormData.append("time_taken", serviceTicketDetails.timeTaken);
    assignFormData.append("status", serviceTicketDetails.status);
    assignFormData.append("assigned_from", user_id);
    assignFormData.append(
      "is_follow_up",
      serviceTicketDetails.status === "follow_up" ? "true" : "false",
    );
    assignFormData.append("start_date", serviceTicketDetails.startDate);
    assignFormData.append("deadline", serviceTicketDetails.dueDate);

    serviceTicketDetails.serviceImages.forEach((item) => {
      const fileToSend = item instanceof File ? item : item.file;
      if (fileToSend) {
        assignFormData.append(
          "media",
          fileToSend,
          item.name || fileToSend.name,
        );
      }
    });

    if (serviceTicketDetails.serviceAudioDescription) {
      assignFormData.append(
        "audio",
        serviceTicketDetails.serviceAudioDescription,
        "recording.webm",
      );
    }

    const url = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/assignticket`;
    try {
      const res = await axios.post(url, assignFormData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const data = res.data;
      if (data.success) {
        // The backend sends the store group its "Ticket Resolved" message on
        // the first resolution and reports here only when that failed.
        const whatsAppWarning = whatsAppFailureText(data.whatsapp, "Ticket saved");
        if (whatsAppWarning) {
          setSnackBarConfig({
            open: true,
            message: whatsAppWarning,
            severity: "warning",
          });
        }

        // Closing the ticket stopped its work timer on the server. Re-read it
        // so the clock on this very screen stops too — the alternative is a
        // resolved ticket sitting next to a timer that is still counting.
        if (["resolved", "rejected"].includes(serviceTicketDetails.status)) {
          timer.resync?.();
        }

        setOpen(true);
      }
    } catch (error) {
      setSnackBarConfig({
        open: true,
        message: "Something went wrong!",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Loading Screen */}
      {isLoading && <LoadingScreen loadingMessage={`Processing ticket data`} />}

      {/* Ticket Description & Management Form */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 1400,
          mx: "auto",
          px: { xs: 1.5, sm: 2, md: 3 },
          py: 2,
        }}
      >
        <Grid container spacing={3}>
          {/* LEFT: Ticket Details */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                height: "100%",
                borderRadius: 3,
                bgcolor: "#fff",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                p: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 16,
                  mb: 2,
                }}
              >
                Ticket Details
              </Typography>

              <TicketDetails ticketData={ticketData} />
            </Box>
          </Grid>

          {/* RIGHT: Management Form */}
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                borderRadius: 3,
                bgcolor: "#fff",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                p: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 16,
                  mb: 2,
                }}
              >
                Management Form
              </Typography>

              <Box
                sx={{
                  borderRadius: 3,
                  width: "100%",
                  bgcolor: "#fff",
                }}
              >
                {/* Select team member */}
                <Autocomplete
                  options={allStaff}
                  getOptionLabel={(option) => option.name || ""}
                  value={
                    allStaff.find(
                      (staff) => staff.id === serviceTicketDetails.teamMember,
                    ) || null
                  }
                  disabled={serviceTicketDetails.status === "follow_up"}
                  onChange={(event, newValue) => {
                    setServiceTicketDetails((prev) => ({
                      ...prev,
                      teamMember: newValue ? newValue.id : "",
                    }));
                  }}
                  filterOptions={(options, state) => {
                    const searchValue = state.inputValue.toLowerCase();
                    return options.filter((option) =>
                      option.name?.toLowerCase().includes(searchValue),
                    );
                  }}
                  ListboxProps={{ style: { maxHeight: 200 } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Team Member"
                      size="small"
                      variant="outlined"
                      sx={formStyle}
                    />
                  )}
                />

                {/* Start & Due Date Container */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      disabled={
                        editTimeBudget > 0 && !isServiceHead(userInfo.role)
                      }
                      type="date"
                      label="Start Date"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={serviceTicketDetails.startDate}
                      onChange={handleChange("startDate")}
                      sx={formStyle}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      disabled={
                        editTimeBudget > 0 && !isServiceHead(userInfo.role)
                      }
                      type="date"
                      label="Due Date"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={serviceTicketDetails.dueDate}
                      onChange={handleChange("dueDate")}
                      sx={formStyle}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>

                {/* Time Budget & Taken Container */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      disabled={
                        editTimeBudget > 0 && !isServiceHead(userInfo.role)
                      }
                      type="number"
                      label="Time Budget"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={serviceTicketDetails.timeBudget}
                      onChange={handleChange("timeBudget")}
                      sx={formStyle}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography sx={{ fontSize: "12px" }}>
                              mins
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      required
                      type="number"
                      label="Time Taken"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={serviceTicketDetails.timeTaken}
                      onChange={handleChange("timeTaken")}
                      sx={formStyle}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography sx={{ fontSize: "12px" }}>
                              mins
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                {/* Priority & Status */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: 1.5,
                  }}
                >
                  {/* Priority */}
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <Select
                      value={serviceTicketDetails.servicePriorityLevel || ""}
                      onChange={handleChange("servicePriorityLevel")}
                      displayEmpty
                      sx={{
                        backgroundColor: "#FFFCF7",
                        borderRadius: "6px",
                        color: getPriorityColor(
                          serviceTicketDetails.servicePriorityLevel,
                        ),
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: getPriorityColor(
                            serviceTicketDetails.servicePriorityLevel,
                          ),
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: getPriorityColor(
                            serviceTicketDetails.servicePriorityLevel,
                          ),
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: getPriorityColor(
                            serviceTicketDetails.servicePriorityLevel,
                          ),
                        },
                      }}
                    >
                      {priorityOptions.map((opt) => (
                        <MenuItem
                          key={opt.value}
                          value={opt.value}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            color: getPriorityColor(opt.value),
                          }}
                        >
                          {opt.icon}
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Status */}
                  <FormControl size="small" sx={{ flex: 1 }}>
                    {/* <Select
                      value={serviceTicketDetails.status || ""}
                      onChange={handleChange("status")}
                      displayEmpty
                      sx={{
                        backgroundColor: "#FFFCF7",
                        borderRadius: "6px",
                        color: getStatusColor(serviceTicketDetails.status)
                          .textCol,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: getStatusColor(
                            serviceTicketDetails.status,
                          ).borderCol,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: getStatusColor(
                            serviceTicketDetails.status,
                          ).borderCol,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: getStatusColor(
                            serviceTicketDetails.status,
                          ).borderCol,
                        },
                      }}
                    >
                      {[
                        { label: "In Progress", value: "in_progress" },
                        { label: "Resolved", value: "resolved" },
                        { label: "Rejected", value: "rejected" },
                      ].map((opt) => (
                        <MenuItem
                          key={opt.value}
                          value={opt.value}
                          sx={{
                            color: getStatusColor(opt.value).textCol,
                            fontWeight: 500,
                          }}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select> */}
                    <Select
                      value={serviceTicketDetails.status || ""}
                      onChange={handleChange("status")}
                      displayEmpty
                      sx={
                        {
                          /* unchanged */
                        }
                      }
                    >
                      {statusOptions.map((opt) => (
                        <MenuItem
                          key={opt.value}
                          value={opt.value}
                          sx={{
                            color: getStatusColor(opt.value).textCol,
                            fontWeight: 500,
                          }}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Audio */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    border: `1px solid ${theme.lightTheme.placeholder}`,
                    borderRadius: 3,
                    px: 1,
                    py: 0.5,
                    mt: 2,
                    mb: 2,
                    bgcolor: "#FFF",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      width: "100%",
                      gap: 1,
                      px: 1.25,
                      py: 1,
                    }}
                  >
                    {/* Mic Icon */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 56,
                        minHeight: 56,
                        backgroundColor: isRecording
                          ? "#003f3fff"
                          : theme.lightTheme.themeCol,
                        boxShadow: isRecording
                          ? "inset 0px 0px 10px #000000ff"
                          : `inset 0px 0px 15px ${theme.lightTheme.themeCol}`,
                        borderRadius: 100,
                        transition:
                          "background-color 0.4s ease, box-shadow 0.4s ease",
                      }}
                    >
                      <MicNoneOutlinedIcon
                        onClick={handleMicClick}
                        sx={{
                          fontSize: 36,
                          color: isRecording ? "#00a2ffff" : "white",
                          cursor: "pointer",
                        }}
                      />
                    </Box>

                    {/* Record Indicator Bars & Timer */}
                    {isRecording && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                          gap: 1,
                          flex: 1,
                          p: 1,
                          borderRadius: 10,
                          bgcolor: "#F8FFFF",
                          border: "1px solid #EEEEEE",
                          minHeight: 65,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: 24,
                            gap: "2px",
                          }}
                        >
                          {[...Array(25)].map((_, i) => (
                            <Box
                              key={i}
                              sx={{
                                width: 2.5,
                                height: 10,
                                bgcolor: theme.lightTheme.themeCol,
                                borderRadius: 100,
                                animation: `wave 1s ease-in-out infinite`,
                                animationDelay: `${i * 0.15}s`,
                              }}
                            />
                          ))}
                        </Box>
                        <Typography
                          sx={{
                            color: theme.lightTheme.themeCol,
                            fontWeight: 600,
                            minWidth: 60,
                            fontSize: 18,
                          }}
                        >
                          {formatTime(recordTime)}
                        </Typography>
                        <style>
                          {`@keyframes wave {0%, 100% { transform: scaleY(0.4); }50% { transform: scaleY(1.2); }}`}
                        </style>
                      </Box>
                    )}

                    {/* Custom Audio Player */}
                    {audioURL && !isRecording && (
                      <Box
                        sx={{
                          flex: 1,
                          p: 1,
                          borderRadius: 10,
                          bgcolor: "#F8FFFF",
                          border: "1px solid #EEEEEE",
                          minHeight: 65,
                        }}
                      >
                        <audio
                          ref={audioRef}
                          src={audioURL}
                          preload="metadata"
                        />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                          }}
                        >
                          <IconButton onClick={togglePlay}>
                            {isPlaying ? (
                              <PauseRoundedIcon />
                            ) : (
                              <PlayArrowRoundedIcon />
                            )}
                          </IconButton>
                          <Slider
                            min={0}
                            max={duration}
                            value={progress}
                            onChange={handleSliderChange}
                            sx={{
                              flex: 1,
                              color: theme.button.primaryBtn,
                              "& .MuiSlider-thumb": {
                                backgroundColor: theme.button.primaryBtn,
                              },
                            }}
                          />
                          <IconButton onClick={toggleMute}>
                            {isMuted ? (
                              <VolumeOffRoundedIcon />
                            ) : (
                              <VolumeUpRoundedIcon />
                            )}
                          </IconButton>
                          <Typography variant="caption">
                            {formatTime(duration)}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Service description */}
                <TextField
                  label="Enter Service Description"
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  variant="outlined"
                  value={serviceTicketDetails.serviceDescription}
                  onChange={handleChange("serviceDescription")}
                  sx={formStyle}
                />

                {/* Container for Dropzone + Previews */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(4, 1fr)",
                      sm: "repeat(6, 1fr)",
                      md: "repeat(6, 1fr)",
                    },
                    gap: 1,
                  }}
                >
                  {/* Dropzone */}
                  <Box
                    {...getRootProps()}
                    sx={{
                      aspectRatio: "1 / 1",
                      borderRadius: 2,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                      cursor:
                        serviceTicketDetails.serviceImages.length >= 5
                          ? "not-allowed"
                          : "pointer",
                      bgcolor:
                        serviceTicketDetails.serviceImages.length >= 5
                          ? theme.lightTheme.themeCol
                          : "white",
                      border:
                        serviceTicketDetails.serviceImages.length >= 5
                          ? "1px solid #b30000ff"
                          : "2px dashed #aaa",
                      transition: "all 0.3s ease-in-out",
                      width: "100%",
                      maxWidth: 90,
                      minWidth: 70,
                      mx: "auto",
                    }}
                  >
                    <input
                      {...getInputProps()}
                      disabled={serviceTicketDetails.serviceImages.length >= 5}
                    />
                    <CloseRoundedIcon
                      sx={{
                        fontSize: 28,
                        color:
                          serviceTicketDetails.serviceImages.length >= 5
                            ? "#ff6a6aff"
                            : theme.lightTheme.placeholder,
                        transform:
                          serviceTicketDetails.serviceImages.length >= 5
                            ? "rotate(0deg)"
                            : "rotate(-135deg)",
                        transition: "transform 0.3s ease-in-out",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 600,
                        textAlign: "center",
                        mt: 0.5,
                        color:
                          serviceTicketDetails.serviceImages.length >= 5
                            ? "white"
                            : theme.lightTheme.placeholder,
                      }}
                    >
                      {serviceTicketDetails.serviceImages.length >= 5
                        ? "Max 5"
                        : "Upload"}
                    </Typography>
                  </Box>

                  {/* File Previews */}
                  {serviceTicketDetails?.serviceImages?.map((file, index) => (
                    <Box
                      key={index}
                      sx={{
                        aspectRatio: "1 / 1",
                        borderRadius: 2,
                        position: "relative",
                        overflow: "hidden",
                        bgcolor: "#f5f5f5",
                        cursor: "pointer",
                        width: "100%",
                        maxWidth: 90,
                        minWidth: 70,
                        mx: "auto",
                      }}
                      onClick={() => handleFileClick(file)}
                    >
                      {file.type.includes("image") ? (
                        <Box
                          component="img"
                          src={file.preview}
                          alt="preview"
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : file.type.includes("video") ? (
                        <Box
                          component="video"
                          src={file.preview}
                          muted
                          autoPlay
                          loop
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : file.type.includes("pdf") ? (
                        <PictureAsPdfRoundedIcon
                          sx={{
                            fontSize: 48,
                            color: theme.lightTheme.themeCol,
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                          }}
                        />
                      ) : (
                        <InsertDriveFileRoundedIcon
                          sx={{
                            fontSize: 48,
                            color: "#666",
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                          }}
                        />
                      )}

                      {/* Delete Button */}
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteImage(index);
                        }}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          width: { xs: 24, sm: 30, md: 36 },
                          height: { xs: 24, sm: 30, md: 36 },
                          borderRadius: "50%",
                          bgcolor: "rgba(0,0,0,0.7)",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: { xs: 14, sm: 16, md: 18 },
                          fontWeight: "bold",
                          cursor: "pointer",
                          zIndex: 2,
                        }}
                      >
                        ✕
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Show file in full window view */}
                <Dialog
                  open={!!overlayFile}
                  onClose={() => setOverlayFile(null)}
                  fullWidth
                  BackdropProps={{
                    sx: {
                      backgroundColor: "rgba(0,0,0,0.5)",
                      backdropFilter: "blur(8px)",
                      WebkitBackdropFilter: "blur(8px)",
                      transition: "all 0.4s ease-in-out",
                    },
                  }}
                  TransitionProps={{ timeout: 400 }}
                >
                  {overlayFile && (
                    <Box sx={{ textAlign: "center" }}>
                      {overlayFile.type.startsWith("image/") && (
                        <Box
                          sx={{
                            maxWidth: "100%",
                            maxHeight: { xs: "65vh", sm: "80vh", md: "80vh" },
                            overflow: "hidden",
                          }}
                        >
                          <TransformWrapper
                            initialScale={1}
                            minScale={1}
                            maxScale={5}
                            wheel={{ step: 0.1 }}
                            pinch={{ step: 5 }}
                            doubleClick={{ disabled: true }}
                          >
                            {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
                              <TransformComponent>
                                <img
                                  src={
                                    overlayFile.preview ||
                                    URL.createObjectURL(overlayFile.file)
                                  }
                                  alt={overlayFile.name}
                                  style={{
                                    maxWidth: "100%",
                                    maxHeight: "80vh",
                                    objectFit: "contain",
                                    cursor: "grab",
                                  }}
                                />
                              </TransformComponent>
                            )}
                          </TransformWrapper>
                        </Box>
                      )}

                      {overlayFile.type.startsWith("video/") && (
                        <Box
                          sx={{
                            maxWidth: "100%",
                            maxHeight: { xs: "65vh", sm: "80vh", md: "80vh" },
                          }}
                        >
                          <video
                            controls
                            style={{ maxWidth: "100%", maxHeight: "100%" }}
                          >
                            <source
                              src={overlayFile.preview}
                              type={overlayFile.type}
                            />
                            Your browser does not support the video tag.
                          </video>
                        </Box>
                      )}

                      {overlayFile.type === "application/pdf" && (
                        <Box
                          sx={{
                            width: "100%",
                            height: { sm: "80vh", md: "80vh" },
                          }}
                        >
                          <object
                            data={overlayFile.preview}
                            type="application/pdf"
                            width="100%"
                            height="100%"
                          >
                            <Typography sx={{ fontSize: 16 }}>
                              PDF preview is not supported.
                            </Typography>
                          </object>
                        </Box>
                      )}
                    </Box>
                  )}
                </Dialog>

                {/* Save Ticket Button */}
                {!(
                  ticket.status === "recurring" &&
                  !isServiceHead(userInfo.role)
                ) && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mt: 3,
                      mb: 1,
                    }}
                  >
                    <Button
                      onClick={handleSave}
                      sx={{
                        background:
                          "linear-gradient(90deg, #008080 0%, #4428A0 100%)",
                        color: "white",
                        width: "100%",
                        fontSize: 16,
                        boxShadow: `
                  inset -2px -2px 12px rgba(0,0,0,0.5),
                  0px 4px 20px rgba(0,0,0,0.4)
                `,
                        borderRadius: 2.5,
                        py: 1.25,
                      }}
                    >
                      Save Ticket
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Saved Dialog Box */}
      <Dialog
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          bgcolor: "rgba(0, 0, 0, 0.3)",
          backdropFilter: "blur(8px)",
          zIndex: isMobile ? 999 : 99999,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 2,
        }}
        open={open}
      >
        <Box
          sx={{
            padding: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img src={success} />
          <Typography sx={{ mt: 2 }}>Ticket Saved Successfully!</Typography>
          <Typography
            sx={{
              bgcolor: theme.button.primaryBtn,
              color: "#fff",
              padding: "4px 12px",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={() => {
              setOpen(false);
              setTimeout(() => navigate(-1), 300);
            }}
          >
            Done
          </Typography>
        </Box>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackBarConfig.open}
        autoHideDuration={2000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          "&.MuiSnackbar-root": {
            bottom: "56px !important",
          },
        }}
      >
        <Alert
          onClose={handleClose}
          severity={snackBarConfig.severity}
          variant="filled"
        >
          {snackBarConfig.message}
        </Alert>
      </Snackbar>
    </>
  );
};

const formStyle = {
  mb: 2,
  "& .MuiOutlinedInput-root": {
    "& fieldset, & .MuiOutlinedInput-notchedOutline": {},
    "&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline": {},
    "&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.borders.secondaryBorder,
      border: 1,
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: theme.borders.secondaryBorder,
  },
};

export default TicketManagementForm;
